import { useEffect, useMemo, useRef, useState } from 'react';
import { Contact } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { createContext, useContextSelector } from 'use-context-selector';
import { Flow } from '@frontend/auth';
import { sentry } from '@frontend/tracking';

export type MFAStep = 'initial' | 'add' | 'verify' | 'success';
export type VerificationContactMethod = 'email' | 'sms';
export type NewVerificationMethod = {
  method?: VerificationContactMethod;
  address?: string;
  contactId?: string;
};

type MFAContextValue = {
  flow: Flow;
  step: MFAStep;
  newVerificationMethod: NewVerificationMethod;
  prevStep(): void;
  nextStep(): void;
  validateVerificationMethod(contact: Contact): void;
  addVerificationMethod(method: VerificationContactMethod): void;
  addVerificationMethodAddress(address: string, contactId: string): void;
};

const MFAContext = createContext<MFAContextValue>({} as MFAContextValue);

export const STEP_ORDER = ['initial', 'add', 'verify', 'success'] as const;

type Props = {
  children: React.ReactNode;
  flowName: 'mfa-flow-nwx' | 'mfa-flow-gk';
};

/**
 * Context provider for MFA that's used to manage the state of the MFA process.
 */
export function MFAContextProvider({ children, flowName }: Props) {
  const [step, setStep] = useState<MFAStep>(STEP_ORDER[0]);
  const [newVerificationMethod, setNewVerficationMethod] = useState<NewVerificationMethod>({});
  const currentFlow = useRef(Flow.create(flowName));

  // add a breadcrumb to Sentry for MFA step changes
  // this is useful for tracking MFA issues
  useEffect(() => {
    sentry.addBreadcrumb({
      category: 'auth',
      message: 'MFA step changed',
      data: {
        step,
      },
    });
  }, [step]);

  const ctxValue = useMemo(
    () => ({
      flow: currentFlow.current,
      step,
      newVerificationMethod,

      prevStep() {
        let prevStepIndex = STEP_ORDER.findIndex((s) => s === step) - 1;
        if (prevStepIndex < 0) {
          prevStepIndex = 0;
        }
        setStep(STEP_ORDER[prevStepIndex]);
        if (prevStepIndex === 0) {
          setNewVerficationMethod({}); // reset the verification method
          currentFlow.current.complete('abandoned');
          currentFlow.current = Flow.create(flowName); // start a new task
        }
      },

      nextStep() {
        let nextStepIndex = STEP_ORDER.findIndex((s) => s === step) + 1;
        if (nextStepIndex >= STEP_ORDER.length) {
          nextStepIndex = 0;
        }
        setStep(STEP_ORDER[nextStepIndex]);
        if (nextStepIndex === 0) {
          setNewVerficationMethod({}); // reset the verification method if going to initial step
          currentFlow.current = Flow.create(flowName); // start a new task
        }
      },

      // this handles verifying an added but not yet validated method
      validateVerificationMethod(contact: Contact) {
        // set the state
        setNewVerficationMethod({
          method: contact.type === 'EMAIL' ? 'email' : 'sms',
          address: contact.contact,
          contactId: contact.contactId,
        });
        // jump to the verify step
        setStep('verify');
      },

      addVerificationMethod(method: VerificationContactMethod) {
        setNewVerficationMethod((prevVerificationMethod) => ({
          ...prevVerificationMethod,
          method,
        }));
      },

      addVerificationMethodAddress(address: string, contactId: string) {
        setNewVerficationMethod((prevVerificationMethod) => ({
          ...prevVerificationMethod,
          address,
          contactId,
        }));
      },
    }),
    [newVerificationMethod, step, flowName]
  );

  return <MFAContext.Provider value={ctxValue}>{children}</MFAContext.Provider>;
}

export function useMFAContext<T>(selector: (value: MFAContextValue) => T) {
  return useContextSelector(MFAContext, selector);
}
